package com.islamicmedia.shared.presentation

import com.islamicmedia.shared.domain.model.AlternativeItem
import com.islamicmedia.shared.domain.model.AnalysisResult
import com.islamicmedia.shared.domain.model.BookMeta
import com.islamicmedia.shared.domain.model.Comment
import com.islamicmedia.shared.domain.model.DailyLimitNotice
import com.islamicmedia.shared.domain.model.LimitRequestUiState
import com.islamicmedia.shared.domain.model.DisambiguationItem
import com.islamicmedia.shared.domain.model.DisplaySettings
import com.islamicmedia.shared.domain.model.BulkBatchState
import com.islamicmedia.shared.util.sanitizeDisplayTitle
import com.islamicmedia.shared.domain.model.BulkResultStatus
import com.islamicmedia.shared.domain.model.BulkScanState
import com.islamicmedia.shared.domain.model.MediaCandidate
import com.islamicmedia.shared.domain.model.MediaPref
import com.islamicmedia.shared.domain.model.MediaType
import com.islamicmedia.shared.domain.model.mediaPrefKey
import com.islamicmedia.shared.domain.model.PricingConfig
import com.islamicmedia.shared.domain.model.PricingPlan
import com.islamicmedia.shared.domain.model.ReferralInfo
import com.islamicmedia.shared.domain.model.SearchCriteria
import kotlinx.serialization.Serializable

data class ProgressStep(
    val stepNumber: Int,
    val query: String,
    val status: ProgressStatus,
    val durationMs: Long = 0,
    val costUSD: Double = 0.0
)

enum class ProgressStatus {
    SEARCHING,
    COMPLETED,
    ERROR
}

enum class TaskStatus { RUNNING, COMPLETE, ERROR, CANCELLED }

/**
 * Lifecycle record for a single analysis. The user can have multiple of these in flight
 * simultaneously (soft-capped); each carries its own progress state, journey id, and result.
 * Live in `MediaCheckerUiState.activeAnalyses` keyed by `id`.
 */
data class AnalysisTask(
    val id: String,
    val title: String,
    val displayTitle: String,
    val mediaType: MediaType,
    val item: DisambiguationItem,
    val criteria: SearchCriteria,
    val quickCheck: Boolean,
    val status: TaskStatus = TaskStatus.RUNNING,
    val progressSteps: List<ProgressStep> = emptyList(),
    val progressFraction: Float = 0f,
    val maxProgressFraction: Float = 0f,
    val totalConcerns: Int = 11,
    /** Cover resolved right after the disambiguation pick, so the analyzing
     *  screen can show art before the analysis result arrives. */
    val coverUrl: String? = null,
    val phase1PartialResult: AnalysisResult? = null,
    /** Phase 2 units finished so far, against [phase2UnitsTotal] — drives the real percentage on the
     *  details-section loader while [phase1PartialResult] is on screen. Seeded from the server's
     *  `phase2_start` (which reports units already cached from a partial earlier run) and then
     *  incremented per completion event. */
    val phase2UnitsDone: Int = 0,
    /** Total Phase 2 units, straight from the server's `phase2_start`. 0 until that arrives, which
     *  is the UI's cue that it has no honest denominator yet and must not show a percentage. */
    val phase2UnitsTotal: Int = 0,
    val result: AnalysisResult? = null,
    val failedPhase2Units: List<String> = emptyList(),
    val retryingUnits: Set<String> = emptySet(),
    val journeyId: String? = null,
    /** Handle for cancelling this analysis server-side. Generated per run, so unlike [journeyId]
     *  (which only exists after a disambiguation step) it is ALWAYS available — an analysis started
     *  from autocomplete, a recommendation card or a deep link is just as cancellable. */
    val cancelToken: String? = null,
    val costUSD: Double = 0.0,
    val startedAtMs: Long,
    /** When the last SSE frame arrived (heartbeats included). The stream-liveness watchdog reads
     *  this to tell "Phase 2 is still grinding" apart from "the socket died and nothing noticed".
     *  Defaults to [startedAtMs] via the ViewModel so a task that never gets a single frame is
     *  still covered. */
    val lastEventAtMs: Long = startedAtMs,
    val completedAtMs: Long? = null,
    val errorMessage: String? = null,
    val internalError: String? = null,
    val retryCount: Int = 0,
    val resumeAttempted: Boolean = false,
    val wasCacheHit: Boolean = false,
    /** True when the task was cancelled because the server returned 402/no_credits.
     *  Tapping it in the strip should re-prompt the paywall (not open the analyzing modal). */
    val paywallBlocked: Boolean = false,
    /** True when the task was cancelled because the server returned 401/auth_required. */
    val authBlocked: Boolean = false,
)

/** The parent-side preview of a kid's result (the detail view's flip card). */
sealed class KidsPreviewState {
    object Loading : KidsPreviewState()
    data class Ready(val result: com.islamicmedia.shared.domain.model.KidsResult) : KidsPreviewState()
    object Failed : KidsPreviewState()
}

@Serializable
data class HistoryItem(
    val id: String,
    val title: String,
    val mediaType: MediaType,
    val result: AnalysisResult,
    val costUSD: Double,
    val timestamp: Long,
    /** Checked by a child in Kids Mode — Your Analyses shows a 🧒 badge. */
    val kidScan: Boolean = false
)

data class MediaCheckerUiState(
    val searchQuery: String = "",
    val searchCriteria: SearchCriteria = SearchCriteria.DEFAULT,
    /** User "Display style" preference — analysis text size + color (persisted). */
    val displaySettings: DisplaySettings = DisplaySettings.DEFAULT,
    val isSearching: Boolean = false,
    val isRecognizingText: Boolean = false,
    val disambiguationItems: List<DisambiguationItem> = emptyList(),
    val showDisambiguation: Boolean = false,
    /** Search-stage selection used for auth/paywall gating before a task is created. */
    val selectedTitle: String? = null,
    val detectedMediaType: MediaType? = null,
    /** Local autocomplete suggestions for the current [searchQuery], from the cached titles index. */
    val suggestions: List<DisambiguationItem> = emptyList(),
    /** Recent successful search queries (most-recent first); shown when the box is focused + empty. */
    val recentSearches: List<String> = emptyList(),
    val errorMessage: String? = null,
    val internalError: String? = null,

    /**
     * Media items found in the last photo when there's more than one to choose from. Non-empty
     * → the "which one did you mean?" chooser is shown; picking one runs a search, dismissing clears it.
     */
    val imageCandidates: List<MediaCandidate> = emptyList(),

    /**
     * Bulk spine scan (library accounts): null = not in use; otherwise the scan flow's state, from
     * picking a photo through reviewing the books it found. See [MediaCheckerViewModel.openBulkScan].
     */
    val bulkScan: BulkScanState? = null,

    /**
     * The running or just-finished bulk batch, kept apart from [bulkScan] on purpose: the screens can be
     * closed (or a book opened) while the batch carries on, and the main screen shows a way back to it
     * for as long as this is non-null.
     */
    val bulkBatch: BulkBatchState? = null,

    /**
     * Active and recently-completed analyses for this session, keyed by local task id.
     * RUNNING entries show a live progress bar in the panel; COMPLETE/ERROR entries stay
     * here for the session so the user can revisit them. On app restart this map is empty
     * and only `history` is restored.
     */
    val activeAnalyses: Map<String, AnalysisTask> = emptyMap(),
    /** When non-null, the UI shows the detail view (progress / results / error) for this task. */
    val viewingTaskId: String? = null,
    /** Pulsed true when the user attempts to start a 6th concurrent analysis. */
    val showSoftCapWarning: Boolean = false,

    /** Persisted history of completed analyses. Older items not in `activeAnalyses` come from here. */
    val history: List<HistoryItem> = emptyList(),
    /** True once the remote history sync has finished (success, empty, or failure). Lets the UI wait
     *  for the "Your Analyses" strip's presence to settle before placing the credits coachmark —
     *  otherwise the strip appearing flips the content alignment and yanks the coachmark target.
     *  Also drives the strip's "Loading…" line on a first login, where the local cache is empty and
     *  the sync takes seconds: false + signed in + no entries == still checking. Because it flips
     *  true on the empty and failure paths too, that line can never get stuck. */
    val historyLoaded: Boolean = false,
    val viewingHistoryItem: HistoryItem? = null,
    /** On a Kids Mode check: what the child saw, fetched as soon as the detail view opens so the flip
     *  card has it ready. Null on every other analysis. */
    val kidsPreview: KidsPreviewState? = null,
    /** The detail card is flipped over to show [kidsPreview]. */
    val kidsFlipped: Boolean = false,
    /** True while the currently-viewed result's HIGHLIGHTS are still being generated/fetched
     *  server-side (Haiku, fire-and-forget after `complete`). Drives the highlights skeleton so
     *  the section doesn't just pop in after the rest of the analysis has rendered. */
    val highlightsLoading: Boolean = false,
    /** Keys (see [flagKey]) of analyses the user has flagged this session — drives the "Flagged" detail state. */
    val flaggedAnalyses: Set<String> = emptySet(),

    val showPaywall: Boolean = false,
    /** Non-null while the fair-use "Daily limit reached" dialog is up. Set by the pre-flight (from
     *  the entitlement) or by the server's `daily_cap` rejection; cleared by dismissDailyLimitNotice. */
    val dailyLimitNotice: DailyLimitNotice? = null,
    /** The "Request additional searches" form inside the weekly-limit dialog. Hidden unless the person
     *  opened it; reset when the limit dialog is dismissed and on sign-out. */
    val limitRequest: LimitRequestUiState = LimitRequestUiState.Hidden,
    /** A library / school account has used every search it was granted: show the "email us" notice
     *  (not the paywall, not the error dialog). Cleared by dismissLibraryOutOfSearches. */
    val showLibraryOutOfSearches: Boolean = false,

    // ── Voucher redemption (the "Have a voucher?" modal on the paywall) ──────────────────────
    /** Is the code-entry modal open? */
    val showVoucherModal: Boolean = false,
    /** A redemption request is in flight — disables the submit button. */
    val voucherLoading: Boolean = false,
    /** Server verdict for the last attempt, or null before one. One of:
     *  not_found | disabled | expired | already_redeemed | already_subscribed | rate_limited | error.
     *  Success isn't stored here — it closes the modal and resumes the blocked analysis. */
    val voucherError: String? = null,
    /** Days granted by a successful redemption, for the confirmation toast. Null otherwise. */
    val voucherGrantedDays: Int? = null,

    // ── Referrals. Words for all of these: ReferralCopy. ────────────────────────────────────
    // The member's own side ("Refer a friend" screen, loaded by loadReferral()):
    /** The last GET /api/referral/me answer; kept while a reload is in flight so the screen doesn't
     *  flash empty. Check isOk / isNotEligible / isDisabled on it. Null before the first load. */
    val referralInfo: ReferralInfo? = null,
    /** A /api/referral/me request is in flight. */
    val referralLoading: Boolean = false,
    /** "error" when the last load failed (show ReferralCopy.ERR_GENERIC + RETRY); null otherwise. */
    val referralError: String? = null,
    // The friend's side ("Have a referral code?" on the paywall):
    /** Is the referral-code dialog open? */
    val showReferralCodeModal: Boolean = false,
    /** An attach request is in flight — disables Apply. */
    val referralCodeLoading: Boolean = false,
    /** Verdict of the last failed attempt, or null. One of: not_found | self | not_new |
     *  already_attached | referrer_ineligible | rate_limited | error. Show it with
     *  ReferralCopy.errorText(). Success isn't stored here — it closes the dialog. */
    val referralCodeError: String? = null,
    /** The canonical code attached to this account in THIS session (typed in the dialog, or flushed
     *  from a /r/ link after sign-in). Non-null shows ReferralCopy.APPLIED_CHIP on the paywall and
     *  hides the "Have a referral code?" link. Not restored on relaunch: nothing reports it back. */
    val referralAttachedCode: String? = null,
    // The friend's side, entered AFTER subscribing ("Got a code from a friend?" on the member's own
    // Refer a friend screen, offered only while referralInfo?.canRedeem == true):
    /** Is the redeem dialog open? */
    val showRedeemCodeModal: Boolean = false,
    /** A POST /api/referral/redeem request is in flight — disables Apply. */
    val redeemCodeLoading: Boolean = false,
    /** Verdict of the last failed attempt, or null. One of: not_found | self | not_new | too_late |
     *  not_subscribed | already_referred | referrer_ineligible | blocked | rate_limited | error.
     *  Show it with ReferralCopy.errorText(). Success isn't stored here — it closes the dialog. */
    val redeemCodeError: String? = null,
    /** Set by a successful redeem, so the screen can show a one-time confirmation (true even when
     *  [redeemSuccessNextBill] is null — then show ReferralCopy.REDEEM_SUCCESS_NO_DATE instead of
     *  redeemSuccess(date)). Cleared by onRedeemSuccessConsumed(). */
    val redeemSucceeded: Boolean = false,
    /** The redeemed member's new next-bill date (ISO), only alongside [redeemSucceeded]. Format with
     *  `com.islamicmedia.shared.domain.model.formatReferralDate` and pass to
     *  ReferralCopy.redeemSuccess(); null shows ReferralCopy.REDEEM_SUCCESS_NO_DATE instead. */
    val redeemSuccessNextBill: String? = null,
    // The friend's side, offered PROACTIVELY on the home screen ("Did a friend send you a code?",
    // ReferralCopy.REDEEM_PROMPT_*) rather than waiting for the member to find the Free Gift
    // screen's link themselves:
    /** True once a FRESH GET /api/referral/me (never one restored from [ReferralInfo]'s on-device
     *  cache — see MediaCheckerViewModel.loadReferral) answered canRedeem == true for an
     *  account+backend this device has never shown the popup to (SettingsStorage.
     *  hasSeenReferralRedeemPrompt). The UI (MainScreen / Index.kt) shows the popup the moment the
     *  home screen is otherwise clear — which may not be the instant this turns true — then calls
     *  onReferralRedeemPromptShown(), which marks the account seen on the device and clears this
     *  flag, so it can only ever fire once per account+backend, ever. */
    val referralRedeemPromptEligible: Boolean = false,
    // The one-time "Your referral code couldn't be used for this purchase" notice (owner's report,
    // 2026-09-25: a friend typed a code, saw the applied chip, and at their first payment the
    // server voided it — e.g. a card match — and they were told nothing).
    /** True once a FRESH GET /api/referral/me (same "fresh only" rule as
     *  [referralRedeemPromptEligible] — never one restored from [ReferralInfo]'s on-device cache)
     *  answered referralBlocked == true for a block (account+backend, referralBlockedAt) this
     *  device has not yet acknowledged (SettingsStorage.hasAcknowledgedReferralBlocked). The UI
     *  (MainScreen / Index.kt) shows the notice under the same "screen clear" condition as the
     *  redeem popup, and never both at once, then calls onReferralBlockedNoticeAcknowledged(),
     *  which persists the acknowledgement and clears this flag. A later, different block
     *  (a new referralBlockedAt) can turn this true again for the same account. */
    val referralBlockedNoticeEligible: Boolean = false,
    /** Admin-controlled paywall plans (GET /api/pricing), fetched on launch. Seeded with the built-in
     *  defaults so the paywall renders instantly and never flashes empty before the fetch resolves. */
    val pricingPlans: List<PricingPlan> = PricingConfig.DEFAULT.plans,
    /** Admin-controlled paywall destination — see [PricingConfig.paywallMode]. Defaults to the
     *  in-app Stripe checkout, so a failed/absent config never changes where buyers are sent. */
    val paywallMode: String = PricingConfig.MODE_STRIPE,
    val thriveCartUrl: String? = null,
    /** How many free full analyses a registered account gets (admin Limits page), from GET /api/pricing.
     *  Null until that arrives, or from a server that predates the setting: the signed-out search-screen
     *  pill then promises [com.islamicmedia.shared.domain.model.DEFAULT_FREE_FULL_LIMIT]. */
    val freeFullLimit: Int? = null,
    /** Hour of day (0–23) when the app was opened — drives the time-based home greeting. */
    val currentHourOfDay: Int = 12,
    val requiresAuth: Boolean = false,
    val isBackgroundAnalysis: Boolean = false,
    /** When true, the next analysis runs the fast-path (single search + single Sonnet scoring). Defaults to Full analysis (false) so the filters "+" stays visible. */
    val quickCheck: Boolean = false,
    /** Pulsed true when the OS store review card should be requested (after every 5th completed
     *  analysis, mobile only, never in Kids Mode: see ReviewPromptRule). Consumed by
     *  InAppReviewEffect, whose onComplete calls onReviewPromptShown() to reset it. */
    val triggerReviewPrompt: Boolean = false,

    /** True while an ISBN scan is being resolved to book metadata. */
    val isResolvingIsbn: Boolean = false,
    /** When non-null, the scanned-book confirm card is shown; confirming starts the analysis. */
    val pendingBook: BookMeta? = null,

    // ── Alternatives / Similar Titles (live-fetched for the currently-viewed result) ──
    /** Green, same-type recommendations for the result currently on screen. Fetched live from
     *  /api/alternatives whenever a result becomes visible; never persisted. */
    val alternatives: List<AlternativeItem> = emptyList(),
    /** True while the alternatives fetch is in flight. The inline section stays hidden until items
     *  actually arrive — this only drives the standalone Alternatives screen's placeholder. */
    val isLoadingAlternatives: Boolean = false,
    /** "More Islamic Picks" — highest islamic_score titles of the same media type, fetched in the
     *  same /api/alternatives call as [alternatives]. Independent of cosine similarity. */
    val islamicAlternatives: List<AlternativeItem> = emptyList(),
    /** "More by this Author" — other green titles by the source's author, fetched in the same
     *  /api/alternatives call. Empty when the source has no resolved author. */
    val authorAlternatives: List<AlternativeItem> = emptyList(),
    /** When non-null, the standalone "Alternatives to {title}" screen is shown (push deep-link target). */
    val alternativesScreenTitle: String? = null,

    // ── Comments / Reactions (shared, public — live-fetched for the currently-viewed result) ──
    /** Shared comment thread for the title currently on screen. Never persisted — fetched live
     *  from GET /api/comments whenever a result becomes visible. */
    val comments: List<Comment> = emptyList(),
    val likeCount: Int = 0,
    val dislikeCount: Int = 0,
    /** The signed-in caller's own reaction: 1 (like), -1 (dislike), or null (none). */
    val myReaction: Int? = null,
    /** True while the comments/reactions fetch is in flight. */
    val isLoadingComments: Boolean = false,
    /** True while a post/edit/delete/react action is in flight (disables the input briefly). */
    val isSubmittingComment: Boolean = false,
    /** Set when a comment action fails, so the UI can show a lightweight inline error. */
    val commentActionError: String? = null,

    /** Per-title pin/hide state for the "Your Analyses" list, keyed by [mediaPrefKey].
     *  Server-synced (see /api/media-prefs) so pins/hides follow the user across devices. */
    val mediaPrefs: Map<String, MediaPref> = emptyMap(),
)

/** The task the user is currently looking at in detail view, or null when on the list/search screen. */
val MediaCheckerUiState.viewedTask: AnalysisTask?
    get() = viewingTaskId?.let { activeAnalyses[it] }

/** Number of analyses currently in the RUNNING state, used for the soft cap. */
val MediaCheckerUiState.runningAnalysisCount: Int
    get() = activeAnalyses.values.count { it.status == TaskStatus.RUNNING }

/** Stable key identifying a flagged analysis (title + media type) for the session-level flagged set. */
fun flagKey(title: String, mediaType: MediaType?): String =
    "${title.trim().lowercase()}::${mediaType?.name ?: ""}"

/**
 * Unified row model rendered by the "Your analyses" strip on the search screen. Wraps both
 * in-flight/just-finished AnalysisTask entries and persisted HistoryItem entries so the strip
 * doesn't have to know about the difference. Plain class (not data class) because the lambda
 * fields aren't worth comparing structurally.
 */
class AnalysisStripEntry(
    val id: String,
    val title: String,
    val mediaType: MediaType,
    val status: TaskStatus,
    val progressFraction: Float,
    /** Final score, when complete. Null while running or on error/cancel. */
    val score: Int?,
    /** Tagged concerns on the result — with [score] this drives the verdict wording (a 10/10
     *  with zero tagged concerns reads "None" rather than "Few"). */
    val concernCount: Int = 0,
    /** Used for ordering inside each status group — most recent first. */
    val sortKey: Long,
    val onClick: () -> Unit,
    /** Non-null only when the entry can be cancelled (i.e. status == RUNNING). */
    val onCancel: (() -> Unit)?,
    /** Poster/cover for the leading thumbnail. Null → branded media-type placeholder. */
    val coverUrl: String? = null,
    /** True when this entry is a quick check (not a full analysis) — drives the top-right card badge. */
    val quickCheck: Boolean = false,
    /** Author line — shown on the card for BOOKS. Null for other media (or unknown). */
    val author: String? = null,
    /** Epoch-millis this title was pinned, or null when unpinned. Pinned cards sort above
     *  everything else, most recently pinned first, and show a pin indicator. */
    val pinnedAtMs: Long? = null,
    /** Overflow-menu actions. Null on entries that can't be pinned/hidden (e.g. still running,
     *  or a signed-out session where the prefs can't be persisted). */
    val onTogglePin: (() -> Unit)? = null,
    val onHide: (() -> Unit)? = null,
    /** Checked by a child in Kids Mode — the card shows a 🧒 tag. */
    val kidScan: Boolean = false,
)

/**
 * Build the merged list of strip entries from this session's activeAnalyses + persisted history.
 * Deduplicates by lowercased title (an active entry wins over a stale history entry for the same
 * title). Running tasks are pinned to the top, sorted newest-first; everything else follows in
 * recency order.
 */
fun MediaCheckerUiState.buildStripEntries(
    onViewTask: (String) -> Unit,
    onHistoryClick: (HistoryItem) -> Unit,
    onCancelRequest: (String) -> Unit,
    /** Pin/hide handlers. Left null (the default) on surfaces that don't offer the card menu. */
    onTogglePin: ((title: String, mediaType: MediaType) -> Unit)? = null,
    onHide: ((title: String, mediaType: MediaType) -> Unit)? = null,
    /** Opens the bulk-scan batch. Tapping one of its still-running books does this. Null = not offered. */
    onViewBulkBatch: (() -> Unit)? = null,
): List<AnalysisStripEntry> {
    val active = activeAnalyses.values
        // A title can have BOTH a quick check and its full-analysis upgrade present at once (you ran
        // a quick check, then tapped "Full analysis available"). Collapse to one card per title,
        // preferring the FULL over the quick check, then RUNNING, then most recent — otherwise the
        // grid shows a duplicate card until the next launch (history reload dedups by title).
        .groupBy { it.displayTitle.trim().lowercase() }
        .map { (_, tasks) ->
            tasks.sortedWith(
                compareByDescending<AnalysisTask> { !it.quickCheck }
                    .thenByDescending { it.status == TaskStatus.RUNNING }
                    .thenByDescending { it.completedAtMs ?: it.startedAtMs }
            ).first()
        }
        .map { task ->
        AnalysisStripEntry(
            id = task.id,
            title = task.displayTitle,
            mediaType = task.mediaType,
            status = task.status,
            progressFraction = task.progressFraction,
            score = task.result?.score,
            concernCount = task.result?.concerns?.size ?: 0,
            sortKey = task.completedAtMs ?: task.startedAtMs,
            onClick = { onViewTask(task.id) },
            onCancel = if (task.status == TaskStatus.RUNNING) {
                { onCancelRequest(task.id) }
            } else null,
            coverUrl = task.result?.coverUrl ?: task.item.coverUrl,
            quickCheck = task.quickCheck,
            author = task.result?.author ?: task.item.author,
            pinnedAtMs = prefFor(task.displayTitle, task.mediaType)?.pinnedAtMs(),
            // A still-running analysis has no settled entry to pin/hide yet.
            onTogglePin = if (task.status == TaskStatus.RUNNING) null
                else onTogglePin?.let { cb -> { cb(task.displayTitle, task.mediaType) } },
            onHide = if (task.status == TaskStatus.RUNNING) null
                else onHide?.let { cb -> { cb(task.displayTitle, task.mediaType) } },
        )
    }
    // Bulk-scan books being analysed on the server have no local task, so they are added here as running
    // cards. A book that has finished shows its own history card instead (the view model copies it in), so
    // it only keeps a running card for the moment between "finished" and "saved".
    val historyTitles = history.map { it.title.trim().lowercase() }.toSet()
    val bulkEntries = bulkBatch?.results.orEmpty().filter { it.status != BulkResultStatus.FAILED && it.status != BulkResultStatus.REFUSED }
        .mapNotNull { item ->
            val displayTitle = (if (!item.year.isNullOrBlank()) "${item.title} (${item.year})" else item.title).sanitizeDisplayTitle()
            if (item.status == BulkResultStatus.DONE && displayTitle.trim().lowercase() in historyTitles) return@mapNotNull null
            AnalysisStripEntry(
                id = "bulk-${item.id}",
                title = displayTitle,
                mediaType = MediaType.BOOKS,
                status = TaskStatus.RUNNING,
                progressFraction = when (item.status) { BulkResultStatus.DONE -> 0.97f; BulkResultStatus.RUNNING -> 0.5f; else -> 0.03f },
                score = null,
                // Newest-first sorting puts these in the order they were ticked.
                sortKey = Long.MAX_VALUE / 2 - item.position,
                onClick = { onViewBulkBatch?.invoke() },
                onCancel = null,
                coverUrl = item.coverUrl,
                author = item.author.ifBlank { null },
            )
        }
    val activeTitles = (active + bulkEntries).map { it.title.trim().lowercase() }.toSet()
    val pastEntries = history
        .filter { it.title.trim().lowercase() !in activeTitles }
        // Hidden titles drop out of the list. This is presentation-only — the underlying history
        // row survives, so re-analyzing the title (which clears the flag) brings the card back.
        .filter { prefFor(it.title, it.mediaType)?.hidden != true }
        .map { item ->
            AnalysisStripEntry(
                id = "h-${item.id}",
                title = item.title,
                mediaType = item.mediaType,
                status = TaskStatus.COMPLETE,
                progressFraction = 1f,
                score = item.result.score,
                concernCount = item.result.concerns.size,
                sortKey = item.timestamp,
                onClick = { onHistoryClick(item) },
                onCancel = null,
                coverUrl = item.result.coverUrl,
                quickCheck = item.result.quickCheck,
                author = item.result.author,
                pinnedAtMs = prefFor(item.title, item.mediaType)?.pinnedAtMs(),
                onTogglePin = onTogglePin?.let { cb -> { cb(item.title, item.mediaType) } },
                onHide = onHide?.let { cb -> { cb(item.title, item.mediaType) } },
                kidScan = item.kidScan,
            )
        }
    // Order: running first (they're transient and want watching), then pinned titles with the
    // most recently pinned on top, then everything else by recency.
    val (running, rest) = (active + bulkEntries + pastEntries).partition { it.status == TaskStatus.RUNNING }
    val (pinned, unpinned) = rest.partition { it.pinnedAtMs != null }
    return running.sortedByDescending { it.sortKey } +
        pinned.sortedByDescending { it.pinnedAtMs } +
        unpinned.sortedByDescending { it.sortKey }
}

/** Pin/hide pref for a title, or null when the user has never pinned/hidden it. */
private fun MediaCheckerUiState.prefFor(title: String, mediaType: MediaType): MediaPref? =
    mediaPrefs[mediaPrefKey(title, mediaType)]

/** Pin timestamp as epoch-millis for sorting; null when unpinned or unparseable. */
private fun MediaPref.pinnedAtMs(): Long? = pinnedAt?.let {
    try { kotlinx.datetime.Instant.parse(it).toEpochMilliseconds() } catch (_: Exception) { null }
}
